import {
  Button,
  ConfirmSheet,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Loading,
  PageHeader,
  SectionHead,
  Select,
  Sheet,
} from '@didto/ui';
import { useQuery } from '@tanstack/react-query';
import type { CSSProperties } from 'react';
import { useState } from 'react';
import { useSession } from '@/app/session';
import { useCommand } from '@/app/useCommand';
import type { Row } from '@/lib/db';
import { errorText } from '@/lib/errors';
import { useOnline } from '@/lib/network';
import { supabase } from '@/lib/supabase';
import './settings.css';

type Route = Pick<Row<'production_route'>, 'id' | 'code' | 'name' | 'color' | 'archived_at'>;
type Station = Pick<
  Row<'production_station'>,
  | 'id'
  | 'code'
  | 'name'
  | 'color'
  | 'production_route_id'
  | 'archived_at'
  | 'warn_seconds'
  | 'late_seconds'
>;
/** KDS ageing overrides in seconds; null falls back to the business setting (Phase 5). */
interface AgeOverrides {
  warn_seconds: number | null;
  late_seconds: number | null;
}

/** A route's or station's own colour, the one value set inline (the dot reads it). */
const ownColour = (color: string | null): CSSProperties | undefined =>
  color ? ({ '--route-color': color } as CSSProperties) : undefined;

/** Production routes (catalog concept, business-wide) and stations (screens, per location). */
export function StationsPage() {
  const s = useSession();
  const locationId = s.me?.active_location_id ?? '';
  const canManage = s.can('station.manage');
  const cmd = useCommand([['routes'], ['stations']]);
  const [editing, setEditing] = useState<Station | 'new' | null>(null);
  const [editingRoute, setEditingRoute] = useState<Route | 'new' | null>(null);

  const routes = useQuery({
    queryKey: ['routes'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('production_route')
        .select('id, code, name, color, archived_at')
        .is('archived_at', null)
        .order('sort');
      if (error) throw error;
      return data;
    },
  });
  const stations = useQuery({
    queryKey: ['stations', locationId],
    enabled: !!locationId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('production_station')
        .select(
          'id, code, name, color, production_route_id, archived_at, warn_seconds, late_seconds',
        )
        .eq('location_id', locationId)
        .is('archived_at', null)
        .order('sort');
      if (error) throw error;
      return data;
    },
  });
  const routeName = (id: string) => routes.data?.find((r) => r.id === id)?.name ?? '—';

  return (
    <div className="d-page">
      <PageHeader
        eyebrow="Settings"
        title="Production stations"
        subtitle={`Routes are where a product is made ("Kitchen", "Bar"). Stations are the screens that show a route's tickets at this location. A product with no route is not sent anywhere.`}
      />
      <section className="d-stack d-stack--tight">
        <SectionHead title="Routes">
          {canManage && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setEditingRoute('new')}
              data-testid="new-route"
            >
              New route
            </Button>
          )}
        </SectionHead>
        {routes.isLoading && <Loading label="Loading routes…" />}
        {routes.isError && (
          <ErrorState
            title="Could not load the routes"
            message={errorText(routes.error)}
            onRetry={() => void routes.refetch()}
          />
        )}
        {routes.isSuccess && routes.data.length === 0 && <EmptyState title="No routes yet" />}
        {(routes.data?.length ?? 0) > 0 && (
          <div className="stations__routes">
            {(routes.data ?? []).map((r) =>
              canManage ? (
                <button
                  key={r.id}
                  type="button"
                  className="stations__route"
                  style={ownColour(r.color)}
                  onClick={() => setEditingRoute(r)}
                  data-testid={`route-${r.code}`}
                >
                  <span className="stations__dot" aria-hidden />
                  <span>
                    <span className="d-mono">{r.code}</span> · {r.name}
                  </span>
                </button>
              ) : (
                <span
                  key={r.id}
                  className="stations__route"
                  style={ownColour(r.color)}
                  data-testid={`route-${r.code}`}
                >
                  <span className="stations__dot" aria-hidden />
                  <span>
                    <span className="d-mono">{r.code}</span> · {r.name}
                  </span>
                </span>
              ),
            )}
          </div>
        )}
      </section>
      <section className="d-stack d-stack--tight">
        <SectionHead title="Stations at this location">
          {canManage && (
            <Button
              size="sm"
              variant="primary"
              onClick={() => setEditing('new')}
              data-testid="new-station"
            >
              New station
            </Button>
          )}
        </SectionHead>
        <div className="d-list" data-testid="station-list">
          {stations.isLoading && <Loading rows={2} label="Loading stations…" />}
          {stations.isError && (
            <ErrorState
              title="Could not load the stations"
              message={errorText(stations.error)}
              onRetry={() => void stations.refetch()}
            />
          )}
          {(stations.data ?? []).map((st) => (
            <div key={st.id} className="d-row" data-testid={`station-row-${st.code}`}>
              <div className="d-row__main">
                <div className="d-row__title">
                  {st.color && (
                    <span className="stations__dot" style={ownColour(st.color)} aria-hidden />
                  )}
                  {st.name} <span className="d-mono d-faint">{st.code}</span>
                </div>
                <div className="d-row__meta">
                  Route: {routeName(st.production_route_id)}
                  {st.warn_seconds !== null || st.late_seconds !== null
                    ? ` · ageing ${st.warn_seconds ?? 'default'} / ${st.late_seconds ?? 'default'} s`
                    : ''}
                </div>
              </div>
              {canManage && (
                <div className="d-actions">
                  <Button size="sm" variant="ghost" onClick={() => setEditing(st)}>
                    Edit
                  </Button>
                </div>
              )}
            </div>
          ))}
          {stations.data?.length === 0 && <EmptyState title="No stations at this location" />}
        </div>
      </section>
      {editing && (
        <StationSheet
          station={editing === 'new' ? null : editing}
          routes={routes.data ?? []}
          onClose={() => setEditing(null)}
          onSave={async (v, overrides) => {
            if (editing === 'new')
              await cmd.mutateAsync({
                kind: 'station.create',
                payload: { location_id: locationId, ...v },
              });
            else
              await cmd.mutateAsync({
                kind: 'station.update',
                payload: { station_id: editing.id, ...v, ...overrides },
              });
            setEditing(null);
          }}
          onArchive={
            editing !== 'new'
              ? async () => {
                  await cmd.mutateAsync({
                    kind: 'station.archive',
                    payload: { station_id: editing.id },
                  });
                  setEditing(null);
                }
              : undefined
          }
        />
      )}
      {editingRoute && (
        <RouteSheet
          route={editingRoute === 'new' ? null : editingRoute}
          onClose={() => setEditingRoute(null)}
          onSave={async (v) => {
            if (editingRoute === 'new') await cmd.mutateAsync({ kind: 'route.create', payload: v });
            else
              await cmd.mutateAsync({
                kind: 'route.update',
                payload: { route_id: editingRoute.id, ...v },
              });
            setEditingRoute(null);
          }}
          onArchive={
            editingRoute !== 'new'
              ? async () => {
                  await cmd.mutateAsync({
                    kind: 'route.archive',
                    payload: { route_id: editingRoute.id },
                  });
                  setEditingRoute(null);
                }
              : undefined
          }
        />
      )}
    </div>
  );
}

/** Archive, after a yes: a station or route cannot be brought back from this page. */
function useArchiveConfirm(onArchive?: () => Promise<void>) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  return {
    open,
    busy,
    ask: () => setOpen(true),
    close: () => setOpen(false),
    confirm: () => {
      if (!onArchive) return;
      setBusy(true);
      // on success the edit sheet closes with this one; a refusal is toasted
      void onArchive().catch(() => {
        setBusy(false);
        setOpen(false);
      });
    },
  };
}

function StationSheet({
  station,
  routes,
  onClose,
  onSave,
  onArchive,
}: {
  station: Station | null;
  routes: Route[];
  onClose: () => void;
  onSave: (
    v: {
      name: string;
      code: string;
      production_route_id: string;
      color: string | null;
    },
    overrides: AgeOverrides,
  ) => Promise<void>;
  onArchive?: () => Promise<void>;
}) {
  const online = useOnline();
  const archive = useArchiveConfirm(onArchive);
  const [name, setName] = useState(station?.name ?? '');
  const [code, setCode] = useState(station?.code ?? '');
  const [route, setRoute] = useState(station?.production_route_id ?? routes[0]?.id ?? '');
  const [color, setColor] = useState(station?.color ?? '');
  const [warn, setWarn] = useState(station?.warn_seconds?.toString() ?? '');
  const [late, setLate] = useState(station?.late_seconds?.toString() ?? '');
  const [busy, setBusy] = useState(false);
  return (
    <>
      <Sheet open onClose={onClose} title={station ? `Edit ${station.name}` : 'New station'}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setBusy(true);
            void onSave(
              {
                name: name.trim(),
                code: code.trim().toUpperCase(),
                production_route_id: route,
                color: color || null,
              },
              {
                warn_seconds: warn.trim() ? Number(warn) : null,
                late_seconds: late.trim() ? Number(late) : null,
              },
            ).finally(() => setBusy(false));
          }}
          className="d-stack"
        >
          <Field label="Name">
            {(id) => (
              <Input
                id={id}
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                maxLength={60}
                autoFocus
                data-testid="station-name"
              />
            )}
          </Field>
          <Field label="Code" help="Letters, digits, underscore">
            {(id) => (
              <Input
                id={id}
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                required
                maxLength={20}
                className="d-mono"
                data-testid="station-code"
              />
            )}
          </Field>
          <Field label="Route">
            {(id) => (
              <Select
                id={id}
                value={route}
                onChange={(e) => setRoute(e.target.value)}
                required
                data-testid="station-route"
              >
                {routes.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Colour (optional)">
            {(id) => (
              <Input
                id={id}
                type="color"
                value={color || '#3f8fe0'}
                onChange={(e) => setColor(e.target.value)}
                className="stations__color"
              />
            )}
          </Field>
          {station && (
            <div className="d-grid">
              <Field label="Warning after (seconds)" help="Blank uses kds.warn_seconds">
                {(id) => (
                  <Input
                    id={id}
                    type="number"
                    min={30}
                    max={7200}
                    inputMode="numeric"
                    value={warn}
                    onChange={(e) => setWarn(e.target.value)}
                    className="d-input--num"
                    data-testid="station-warn"
                  />
                )}
              </Field>
              <Field label="Late after (seconds)" help="Blank uses kds.late_seconds">
                {(id) => (
                  <Input
                    id={id}
                    type="number"
                    min={60}
                    max={14400}
                    inputMode="numeric"
                    value={late}
                    onChange={(e) => setLate(e.target.value)}
                    className="d-input--num"
                    data-testid="station-late"
                  />
                )}
              </Field>
            </div>
          )}
          <div className="d-spread">
            {onArchive ? (
              <Button variant="danger" disabled={!online} onClick={archive.ask}>
                Archive
              </Button>
            ) : (
              <span />
            )}
            <Button
              type="submit"
              variant="primary"
              busy={busy}
              disabled={!online}
              data-testid="save-station"
            >
              Save
            </Button>
          </div>
        </form>
      </Sheet>
      {station && onArchive && (
        <ConfirmSheet
          open={archive.open}
          title={`Archive ${station.name}?`}
          confirmLabel="Archive station"
          busy={archive.busy}
          onClose={archive.close}
          onConfirm={archive.confirm}
        >
          <p>
            The station leaves this location: its screen shows no more tickets. Its history stays.
          </p>
          <p className="d-muted">
            A station that a kitchen or bar screen still shows cannot be archived. It cannot be
            brought back from here.
          </p>
        </ConfirmSheet>
      )}
    </>
  );
}

function RouteSheet({
  route,
  onClose,
  onSave,
  onArchive,
}: {
  route: Route | null;
  onClose: () => void;
  onSave: (v: { name: string; code: string; color: string | null }) => Promise<void>;
  onArchive?: () => Promise<void>;
}) {
  const online = useOnline();
  const archive = useArchiveConfirm(onArchive);
  const [name, setName] = useState(route?.name ?? '');
  const [code, setCode] = useState(route?.code ?? '');
  const [color, setColor] = useState(route?.color ?? '');
  const [busy, setBusy] = useState(false);
  return (
    <>
      <Sheet open onClose={onClose} title={route ? `Edit route ${route.name}` : 'New route'}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setBusy(true);
            void onSave({
              name: name.trim(),
              code: code.trim().toUpperCase(),
              color: color || null,
            }).finally(() => setBusy(false));
          }}
          className="d-stack"
        >
          <Field label="Name">
            {(id) => (
              <Input
                id={id}
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                maxLength={60}
                autoFocus
                data-testid="route-name"
              />
            )}
          </Field>
          <Field label="Code">
            {(id) => (
              <Input
                id={id}
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                required
                maxLength={20}
                className="d-mono"
                data-testid="route-code"
              />
            )}
          </Field>
          <Field label="Colour (optional)">
            {(id) => (
              <Input
                id={id}
                type="color"
                value={color || '#e07a3f'}
                onChange={(e) => setColor(e.target.value)}
                className="stations__color"
              />
            )}
          </Field>
          <div className="d-spread">
            {onArchive ? (
              <Button variant="danger" disabled={!online} onClick={archive.ask}>
                Archive
              </Button>
            ) : (
              <span />
            )}
            <Button
              type="submit"
              variant="primary"
              busy={busy}
              disabled={!online}
              data-testid="save-route"
            >
              Save
            </Button>
          </div>
        </form>
      </Sheet>
      {route && onArchive && (
        <ConfirmSheet
          open={archive.open}
          title={`Archive route ${route.name}?`}
          confirmLabel="Archive route"
          busy={archive.busy}
          onClose={archive.close}
          onConfirm={archive.confirm}
        >
          <p>Products can no longer be sent to this route. Its history stays.</p>
          <p className="d-muted">
            A route that stations or products still use cannot be archived: move them first. It
            cannot be brought back from here.
          </p>
        </ConfirmSheet>
      )}
    </>
  );
}
